import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { YearTabs } from "@/components/YearTabs";
import { PastYearResults } from "@/components/PastYearResults";
import { Search, Clock } from "lucide-react";


export default function Scores() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ results: [], last_updated: null });
  const [loaded, setLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [year, setYear] = useState(() => Number(searchParams.get("year")) || null);
  const [years, setYears] = useState([]);
  const navigate = useNavigate();
  const pickYear = (y) => { setYear(y); setSearchParams(y ? { year: String(y) } : {}, { replace: true }); };

  useEffect(() => {
    api.get("/horses/years").then((r) => setYears(r.data.years || [])).catch(() => {});
  }, []);

  const fetchResults = (query) => {
    api.get("/results", { params: query ? { q: query } : {} })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoaded(true));
  };
  useEffect(() => { fetchResults(""); }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchResults(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const results = data.results || [];
  const groups = results.reduce((acc, r) => {
    const last = acc[acc.length - 1];
    if (last && last.division === r.division) last.items.push(r);
    else acc.push({ division: r.division, items: [r] });
    return acc;
  }, []);

  return (
    <AppLayout>
      <PageHeader subtitle={year ? `${year} Final Standings` : "Official Standings"} title={year ? `${year} Results` : "Competition Results"} />
      {years.length > 0 && <YearTabs testid="results-year-tabs" years={years} year={year} setYear={pickYear} />}

      <div className="px-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="results-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by hip #, horse, or trainer"
            className="h-12 w-full rounded-xl border border-border bg-card pl-10 pr-4 text-base outline-none focus:ring-2 focus:ring-secondary" />
        </div>
        {!year && data.last_updated && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock size={13} /> Last updated {new Date(data.last_updated).toLocaleString()}
          </p>
        )}
      </div>

      {year && <PastYearResults year={year} q={q} />}

      {!year && loaded && results.length === 0 && (
        <div className="mx-4 mt-4 rounded-xl border border-yellow-300 bg-yellow-50 p-4 text-sm font-medium text-yellow-800">
          {q ? "No published results match your search." : "No published results are available yet. Results appear here once reviewed and published by event staff."}
        </div>
      )}

      {!year && groups.map((g) => (
      <section key={g.division || "none"} className="px-4 pt-4" data-testid={`results-division-${(g.division || "other").toLowerCase().replace(/\s+/g, "-")}`}>
        <h2 className="mb-2 font-display text-lg font-bold text-primary">{g.division || "Other"}</h2>
      <div className="space-y-2">
        {g.items.map((r) => (
          <button key={r.horse_id} data-testid={`result-${r.hip_number}`} onClick={() => navigate(`/mustangs/${r.horse_id}`)}
            className="w-full rounded-xl border border-border bg-card p-3 text-left shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">HIP #{r.hip_number}</span>
                <div>
                  <p className="font-display text-lg font-bold leading-tight text-primary">{r.horse_name}</p>
                  <p className="text-xs text-muted-foreground">{r.trainer_name} · {r.division}</p>
                </div>
              </div>
              <div className="text-right">
                {r.placing && <p className="font-display text-xl font-bold text-secondary">#{r.placing}{r.tied ? " T" : ""}</p>}
                <StatusBadge status={r.status === "final" ? "Final" : "Published"} />
              </div>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
              <Metric label="H&C" value={r.classes?.hc?.total} />
              <Metric label="Trail" value={r.classes?.trail?.total} />
              <Metric label="Freestyle" value={r.classes?.freestyle?.total} />
              <Metric label="Bonus/Pen" value={`+${r.total_bonus || 0}/-${r.total_penalties || 0}`} />
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Overall {r.tied ? "· Tied" : ""}</span>
              <span className="font-display text-xl font-bold text-primary">{r.overall_total ?? "—"}</span>
            </div>
          </button>
        ))}
      </div>
      </section>
      ))}
    </AppLayout>
  );
}

const Metric = ({ label, value }) => (
  <div className="rounded-lg bg-muted py-1.5">
    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-semibold text-primary">{value ?? "—"}</p>
  </div>
);
